import React from "react";
import { FaRegCalendar } from "react-icons/fa";

/**
 * TaskMetaRow Component
 * 
 * Props:
 * - dueDate: string ("YYYY-MM-DD" or null)
 * - commentCount: number
 * - labels: string[] (array of label names)
 * - assigneeName: string
 * - priority: string
 * - status: string
 * - onLabelClick: function (optional callback when a label tag is clicked)
 * 
 * Renders:
 * - Calendar icon + due date (colored text-orange-500 if today, text-red-500 if overdue, gray otherwise)
 * - Assignee (user icon + name)
 * - Priority (colored dot + name)
 * - Status (colored dot + name)
 * - Comment icon + count (only if count > 0)
 * - A list of LabelTag components for each label
 */
export function TaskMetaRow({ dueDate, commentCount, labels = [], assigneeName, priority, status, onLabelClick, recurrenceText }) {
  // Determine due date coloring
  let dueDateColorClass = "text-gray-400";
  
  if (dueDate) {
    // Parse due date in local time
    const [year, month, day] = dueDate.split("-").map(Number);
    const due = new Date(year, month - 1, day);
    
    // Today's date (midnight) in local time
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueTime = due.getTime();
    const todayTime = today.getTime();
    
    if (dueTime === todayTime) {
      dueDateColorClass = "text-orange-500";
    } else if (dueTime < todayTime) {
      dueDateColorClass = "text-red-500";
    }
  }

  const hasDueDate = !!dueDate;
  const hasPriority = !!priority && priority !== "None";
  const hasRecurrence = !!recurrenceText;

  // If nothing to display, render nothing
  if (!hasDueDate && !hasPriority && !hasRecurrence) {
    return null;
  }

  const priorityColors = {
    Low: "bg-green-500",
    Medium: "bg-yellow-500",
    High: "bg-red-500"
  };
  
  return (
    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-400 font-medium select-none">
      
      {/* Due Date */}
      {hasDueDate && (
        <span className={`inline-flex items-center gap-1 ${dueDateColorClass}`}>
          <FaRegCalendar className="w-3.5 h-3.5" />
          <span>{dueDate}</span>
        </span>
      )}

      {/* Priority */}
      {hasPriority && (
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <span className={`w-1.5 h-1.5 rounded-full ${priorityColors[priority] || "bg-gray-400"}`} />
          <span>{priority}</span>
        </span>
      )}

      {/* Recurrence */}
      {hasRecurrence && (
        <span className="inline-flex items-center gap-1 text-blue-500">
          <span>🔄</span>
          <span>{recurrenceText}</span>
        </span>
      )}
      
    </div>
  );
}
